import React, { createContext, useState } from 'react';
import { AxiosInstance } from 'axios';
import { API_LIVERAY_URL } from '@/config';

import useLaunchDarkly from '@/hooks/useLaunchDarkly';

import { HttpService, THttpServiceConfig } from '@/services/HttpService';

import SessionTokenHelper from '@/helpers/AuthenticationHelper/SessionTokenHelper';
import {
  getApplicationCookie,
  getApplicationCookieGol,
} from '@/helpers/CookieHelper';
import { getEnv } from '@/helpers/EnvHelper';
import { getValue } from '@/helpers/GetValueHelper';
import { getUuid } from '@/helpers/StringHelper/uuid';

import { TApiProps, TParams } from './types';

export const ApiContext = createContext({} as TApiProps);

const ApiProvider = ({ children }: TParams): JSX.Element | null => {
  const { baseURL, gameURL } = useLaunchDarkly();

  const [traceId, setTraceId] = useState('');

  function getApiConfig(): THttpServiceConfig {
    const config = {
      baseURL,
      headers: {
        Accept: 'application/json',
      },
    };

    if (SessionTokenHelper()) {
      return {
        ...config,
        headers: {
          ...config.headers,
          Authorization: `Bearer ${SessionTokenHelper()}`,
        },
      };
    }

    if (getApplicationCookie()) {
      return {
        ...config,
        headers: {
          ...config.headers,
          'x-smiles-recsys': getApplicationCookie(),
        },
      };
    }

    if (getApplicationCookieGol()) {
      return {
        ...config,
        headers: {
          ...config.headers,
          'x-smiles-recsys': getApplicationCookieGol(),
        },
      };
    }

    return config;
  }

  function getApiLiferayConfig(): THttpServiceConfig {
    const env = getEnv();
    return {
      baseURL: API_LIVERAY_URL.replace('{{env}}', env),
      headers: {
        Accept: 'application/json',
      },
    };
  }

  function getApiGameConfig(): THttpServiceConfig {
    const config = {
      baseURL: gameURL,
      headers: {
        Accept: 'application/json',
      },
    };

    if (SessionTokenHelper()) {
      return {
        ...config,
        headers: {
          ...config.headers,
          Authorization: `Bearer ${SessionTokenHelper()}`,
        },
      };
    }

    return config;
  }

  const ApiLiferay = HttpService(getApiLiferayConfig());
  const ApiGame = HttpService(getApiGameConfig());

  const Api = HttpService(getApiConfig());

  /* istanbul ignore next */
  (Api as AxiosInstance).interceptors.request.use(
    config => {
      if (traceId) {
        const method = getValue(config, 'method', '') as string;
        if (method.toLocaleUpperCase() === 'GET') {
          return {
            ...config,
            params: {
              ...config.params,
              trace_id: traceId,
              request_id: getUuid(),
            },
          };
        }

        if (method?.toLocaleUpperCase() === 'POST') {
          return {
            ...config,
            data: { ...config.data, trace_id: traceId, request_id: getUuid() },
          };
        }
      }

      return config;
    },
    error => {
      return Promise.reject(error);
    },
  );

  return (
    <ApiContext.Provider
      value={{
        Api,
        ApiLiferay,
        ApiGame,
        setTraceId,
        traceId,
      }}
    >
      {children}
    </ApiContext.Provider>
  );
};

export default ApiProvider;
